'use client';

import React, { useContext, useEffect, useRef } from 'react';
import { ReactNode } from 'react';
import OTelTraceContextProvider, {
  useOTelTraceContext,
  IProps as ContextProps, getName, OTelTraceContext,
} from './OTelTraceContext';
import { trace } from '@dealroadshow/jsbro/OTel/api';
import { Span } from '@opentelemetry/api/build/src/trace/span';

const OTelInstrumentSpan = (
  { children, reactContext, autoEnd = true }:
  { children: ReactNode, reactContext: React.Context<SpanContext>, autoEnd?: boolean },
) => {
  const { span } = useContext(reactContext);

  useEffect(() => {
    if (autoEnd) {
      span.end();
    }
  }, [span, autoEnd]);

  return children;
};

export const StartActiveSpan = ({
  children,
  name,
  context,
  autoEnd,
  ...otherProps
}: { children: ReactNode, autoEnd?: boolean } & ContextProps) => {
  if (!name) {
    name = getName(children);
  }

  const traceContext = useOTelTraceContext(true);

  if (context !== null) {
    context = traceContext;
  }

  // Context is required for keeping nested structure of elements.
  // In case of using single OTelTraceContextProvider, we cannot determine the hierarchy of elements
  // as React may render children in different order.
  return (
    <OTelTraceContextProvider { ...otherProps } name={ name } context={ context }>
      <OTelInstrumentSpan reactContext={ OTelTraceContext } autoEnd={ autoEnd }>
        { children }
      </OTelInstrumentSpan>
    </OTelTraceContextProvider>
  );
};

// @ts-ignore
export interface SpanContext { span: Span }

export const StartSpan = ({
  children,
  name,
  reactContext: ReactContext,
  options,
  autoEnd,
}: { children: ReactNode, reactContext: React.Context<SpanContext>, autoEnd?: boolean } & ContextProps) => {
  if (!name) {
    name = getName(children);
  }
  const tracer = trace.getTracer();
  const span = tracer.startSpan(name, options);

  // @ts-ignore
  const spanRef = useRef<Span>(span);

  // Context is required for keeping nested structure of elements.
  return (
    <ReactContext.Provider value={ { span: spanRef.current } }>
      <OTelInstrumentSpan reactContext={ ReactContext } autoEnd={ autoEnd }>
        { children }
      </OTelInstrumentSpan>
    </ReactContext.Provider>
  );
};

export const Tracer = {
  StartActiveSpan,
  StartSpan,
};
